import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAccess } from '../lib/access';

const FIELDS = [
  { key: 'door_code', label: 'Door/Lock Code', masked: true },
  { key: 'wifi_name', label: 'WiFi Name' },
  { key: 'wifi_password', label: 'WiFi Password', masked: true },
];

// Door code and WiFi for one property, from property_access_info. The database decides who can see
// the row (owners, managers, admins, crew with codes turned on); a hidden or empty row shows
// "Not available" rather than an error. With `editable`, owners and admins can change it.
export default function AccessInfo({ propertyId, editable = false }) {
  const access = useAccess();
  const [row, setRow] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [shown, setShown] = useState({});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('property_access_info')
      .select('*')
      .eq('property_id', propertyId)
      .maybeSingle();
    setRow(data || null);
    setLoaded(true);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const canEdit = editable && access.can(propertyId, 'admin');

  const save = async () => {
    setSaving(true);
    setError('');
    const payload = { property_id: propertyId, updated_at: new Date().toISOString() };
    FIELDS.forEach(f => { payload[f.key] = form[f.key]?.trim() || null; });
    const { data, error } = await supabase
      .from('property_access_info')
      .upsert(payload, { onConflict: 'property_id' })
      .select('*')
      .single();
    setSaving(false);
    if (error) { setError(error.message); return; }
    setRow(data);
    setEditing(false);
  };

  if (!loaded) return null;

  if (editing) {
    return (
      <div className="access-info">
        {error && <div className="error-msg">{error}</div>}
        <div className="property-info-grid">
          {FIELDS.map(f => (
            <div key={f.key} className="property-info-row">
              <label className="property-info-label">{f.label}</label>
              <input
                className="property-info-input"
                value={form[f.key] || ''}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
              />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save codes'}</button>
          <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="access-info">
      <div className="property-info-grid">
        {FIELDS.map(f => {
          const val = row?.[f.key];
          return (
            <div key={f.key} className="property-info-row">
              <span className="property-info-label">{f.label}</span>
              <span className="property-info-value" data-access-field={f.key}>
                {!val ? 'Not available'
                  : f.masked && !shown[f.key] ? '••••••' : val}
                {f.masked && val && (
                  <button className="mask-toggle" onClick={() => setShown({ ...shown, [f.key]: !shown[f.key] })}>
                    {shown[f.key] ? 'Hide' : 'Show'}
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {canEdit && (
        <button
          className="btn btn-secondary btn-sm"
          style={{ marginTop: 8 }}
          onClick={() => { setForm({ door_code: row?.door_code || '', wifi_name: row?.wifi_name || '', wifi_password: row?.wifi_password || '' }); setEditing(true); }}
        >
          Edit door code &amp; WiFi
        </button>
      )}
    </div>
  );
}
